import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp,
  CheckCircle,
  AlertTriangle,
  Package,
  XCircle,
  DollarSign
} from 'lucide-react';

const StatsCards = ({ orders, setActiveTab, setServicesSubTab, canSeeClientPrices = true }) => {
  const { t } = useTranslation();

  /** Active pipeline: Kanban columns (pool, assigned, pending approval) while not finally approved. */
  const isAdminPipelineActive = (o) => {
    if (o.approval_status === 'approved') return false;
    if (o.approval_status === 'pending_approval') return true;
    const st = o.status;
    if (!st || st === 'delivered' || st === 'completed' || st === 'cancelled') return false;
    if (String(st).toLowerCase().includes('cancelado')) return false;
    const hasDriver = Boolean(o.driver_id && String(o.driver_id).trim());
    const pool = ['pending', 'confirmed', 'por_asignar'];
    if (pool.includes(st) && !hasDriver) return true;
    if (['assigned', 'in_progress', 'in_transit', 'en_route', 'picked_up'].includes(st)) return true;
    if (st === 'pending' && hasDriver) return true;
    if ((st === 'confirmed' || st === 'por_asignar') && hasDriver) return true;
    return false;
  };

  const statsConfig = [
    {
      id: 'in-progress',
      label: t('admin_services_in_progress'),
      value: orders.filter(isAdminPipelineActive).length,
      icon: TrendingUp,
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
      onClick: () => setActiveTab('orders'),
      requiresClientPrices: false
    },
    {
      id: 'total-completed',
      label: t('admin_total_services_done'),
      value: orders.filter(o => o.approval_status === 'approved').length,
      icon: CheckCircle,
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600',
      onClick: () => {
        setActiveTab('orders');
        setServicesSubTab('historical');
      },
      requiresClientPrices: false
    },
    {
      id: 'pending-completion',
      label: t('admin_pending_closure'),
      value: orders.filter(o => 
        o.status === 'completed' && 
        (!o.has_pickup_photos || !o.has_delivery_photos || !o.approval_status)
      ).length,
      icon: AlertTriangle,
      bgColor: 'bg-orange-100',
      iconColor: 'text-orange-600',
      onClick: () => setActiveTab('orders'),
      requiresClientPrices: false
    },
    {
      id: 'completed',
      label: t('admin_services_completed'),
      value: orders.filter(o => o.status === 'completed').length,
      icon: Package,
      bgColor: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      onClick: () => setActiveTab('orders'),
      requiresClientPrices: false
    },
    {
      id: 'pending-payment',
      label: t('admin_pending_payment'),
      value: orders.filter(o => o.payment_status !== 'paid' && o.payment_status !== 'cancelled').length,
      icon: XCircle,
      bgColor: 'bg-red-100',
      iconColor: 'text-red-600',
      onClick: () => setActiveTab('orders'),
      requiresClientPrices: true  // Only visible if can see client prices
    },
    {
      id: 'paid',
      label: t('admin_services_paid'),
      value: orders.filter(o => o.payment_status === 'paid').length,
      icon: DollarSign,
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600',
      onClick: () => setActiveTab('orders'),
      requiresClientPrices: true  // Only visible if can see client prices
    },
    {
      id: 'pending-approvals',
      label: t('admin_pending_approvals'),
      value: orders.filter(o => o.approval_status === 'pending_approval').length,
      icon: AlertTriangle,
      bgColor: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      onClick: () => setActiveTab('approvals'),
      requiresClientPrices: false
    },
    {
      id: 'total-revenue',
      label: t('admin_total_revenue'),
      value: `€${orders.reduce((sum, o) => sum + (o.total_price || 0), 0).toFixed(2)}`,
      icon: DollarSign,
      bgColor: 'bg-[#36e961] bg-opacity-20',
      iconColor: 'text-[#36e961]',
      onClick: () => setActiveTab('orders'),
      requiresClientPrices: true  // Only visible if can see client prices
    }
  ];

  // Filter stats based on permissions
  const visibleStats = statsConfig.filter(stat => 
    !stat.requiresClientPrices || canSeeClientPrices
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {visibleStats.map((stat) => {
        const Icon = stat.icon;
        
        return (
          <div
            key={stat.id}
            className="stat-card cursor-pointer hover:shadow-lg transition-shadow"
            onClick={stat.onClick}
            data-testid={`stat-${stat.id}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">{stat.label}</p>
                <p className="stat-value">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center`}>
                <Icon className={`w-6 h-6 ${stat.iconColor}`} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StatsCards;
